import { cmsToOccDisplayText } from '@cms/utils/cmsToOccDisplayText'
import { FilmCategory, FilmTrailerProvider } from '@vista-digital/ocapi-types/v1'
import type { OrderConfig } from '@vista-digital/occ-domain'
import { BookingFeeMode, TaxMode, TicketingMode } from '@vista-digital/occ-domain'
import type {
    OccAccessibilityText,
    OccAssets,
    OccFilmConfig,
    OccFilterConfig,
    OccMemberConfig,
    OccNotificationConfig,
} from '@vista-digital/occ-ui'
import { defaultOccAccessibilityText } from '@vista-digital/occ-ui/src/lib/context/occ/types/accessibility-text'
import { defaultOccAssets } from '@vista-digital/occ-ui/src/lib/context/occ/types/assets'
import type { OccFormFieldValidationConfig } from '@vista-digital/occ-ui/src/lib/context/occ/types/config/form/form-field-validation'
import type { OccLocaleConfig } from '@vista-digital/occ-ui/src/lib/context/occ/types/config/locale/locale'
import { defaultOccMemberConfig } from '@vista-digital/occ-ui/src/lib/context/occ/types/config/member/member'
import type { OccDisplayText } from '@vista-digital/occ-ui/src/lib/context/occ/types/display-text/display-text'
import { FieldFormatType } from '@vista-digital/occ-ui/src/lib/types/form/field-format-type'
import type { NestedPartial } from '@vista-digital/utilities'
import { CmsCountries, cmsDataService } from '@/content/services/cms.data.service'
import type { OccContextPropsApi } from './models'

// DOMAIN

export function getOccContextDisplayText(
    props: OccContextPropsApi,
    country: CmsCountries
): NestedPartial<OccDisplayText> {
    return {
        film: {
            advanceBookingLabel: props.advanceBookingLabel,
            comingSoonLabel: props.comingSoonLabel,
            nowShowingLabel: props.nowShowingLabel,
            preBookNowLabel: props.preBookNowLabel,
            sponsoredLabel: props.sponsoredLabel,
            runtimeFormat: props.filmRuntimeFormat,
            addToWatchlistButtonLabel: props.addToWatchlistButtonLabel,
            removeFromWatchlistButtonLabel: props.removeFromWatchlistButtonLabel,
            failedToAddFilmToWatchlistMessage: props.failedToAddFilmToWatchlistMessage,
            failedToAddFilmToWatchlistTitle: props.failedToAddFilmToWatchlistTitle,
            failedToRemoveFilmFromWatchlistMessage: props.failedToRemoveFilmFromWatchlistMessage,
            failedToRemoveFilmFromWatchlistTitle: props.failedToRemoveFilmFromWatchlistTitle,
        },
        // Maybe in the CMS, probably not
        seat:
            country === CmsCountries.Spain || country === CmsCountries.Portugal
                ? {
                      labelFormat: '${rowLabel}-${seatLabel}',
                      groupFormat: '${firstSeatLabel} a ${lastSeatLabel}',
                      groupSeparator: ', ',
                      placeholderLabel: {
                          text: '— ',
                          className: 'v-seat-summary-placeholder-label',
                      },
                      areaNameSeparator: ', ',
                  }
                : {
                      labelFormat: '${rowLabel}${seatLabel}',
                      groupFormat: '${firstSeatLabel} - ${lastSeatLabel}',
                      groupSeparator: ', ',
                      placeholderLabel: {
                          text: '— ',
                          className: 'v-seat-summary-placeholder-label',
                      },
                      areaNameSeparator: ', ',
                  },
        form: {
            fieldOptionalSuffix: props.formFieldOptionalSuffix,
            passwordRuleCheckerHelpText: props.passwordRuleCheckerHelpText,
        },
        order: {
            bookingFeeLabel: cmsToOccDisplayText(props.bookingFeeLabelCms),
            includedBookingFeeLabel: cmsToOccDisplayText(props.includedBookingFeeLabelCms),
            itemLimitReachedMessage: props.itemLimitReachedMessage,
            inProgressItemLimitReachedMessage: props.inProgressItemLimitReachedMessage,
            estimatedTaxLabel: props.estimatedTaxLabel,
            inProgressItemRedemptionLimitReachedMessage: props.inProgressItemRedemptionLimitReachedMessage,
            subtotalCostExcludingBookingFeeLabel: props.subtotalCostExcludingBookingFeeLabel,
            subtotalCostIncludingBookingFeeLabel: cmsToOccDisplayText(props.subtotalCostIncludingBookingFeeLabelCms),
            taxLabel: props.taxLabel,
            totalCostLabel: props.totalCostLabel,
            totalRefundedLabel: props.totalRefundedLabel,
            totalRefundableLabel: props.totalRefundableLabel,
            totalSavingsLabel: props.totalSavingsLabel,
            tipPercentageLabel: props.tipPercentageLabel,
            tipPercentageFormat: props.tipPercentageFormat,
            tipLabel: props.tipLabel,
            zeroTipLabel: props.zeroTipLabel,
            changeTipButtonLabel: props.changeTipButtonLabel,
            ticketDetailsFormat: props.ticketDetailsFormat,
            itemModifierDetailsPaidFormat: props.itemModifierDetailsPaidFormat,
            itemModifierDetailsUnpaidFormat: props.itemModifierDetailsUnpaidFormat,
        },
        showtime: {
            soldOutLabel: props.soldOutLabel,
        },
        filter: {
            allOptionLabel: props.allOptionLabel,
            nonRecurringSubscriptionBillingPeriodLabel: props.nonRecurringSubscriptionBillingPeriodLabel,
            oneMonthSubscriptionBillingPeriodLabel: props.oneMonthSubscriptionBillingPeriodLabel,
            recurringSubscriptionBillingPeriodLabel: props.recurringSubscriptionBillingPeriodLabel,
            subscriptionBillingPeriodLabel: props.subscriptionBillingPeriodLabel,
        },
        member: {
            rewardExclusiveLabel: props.loyaltyRewardExclusiveLabel,
            defaultBalanceTypeName: props.defaultBalanceTypeName,
            defaultBalanceTypeUnitName: props.defaultBalanceTypeUnitName,
            friendCodeFormat: props.friendCodeFormat,
            memberBalanceFormat: props.memberBalanceFormat,
        },
        price: {
            discountedPriceFormat: props.discountedPriceFormat,
            freeDiscountLabel: props.freeDiscountLabel,
            itemPriceFormat: props.itemPriceFormat,
            percentageOffDiscountFormat: props.percentageOffDiscountFormat,
            priceOffDiscountFormat: props.priceOffDiscountFormat,
            soldOutLabel: props.concessionSoldOutLabel,
            availableFromTimeLabel: props.availableFromTimeLabel,
            availableFromDayLabel: props.availableFromDayLabel,
            unavailableForShowtimeLabel: props.unavailableForShowtimeLabel,
        },
        subscription: {
            recurringOneMonthBillingPeriodLabel: props.recurringOneMonthBillingPeriodLabel,
            recurringTwelveMonthsBillingPeriodLabel: props.recurringTwelveMonthsBillingPeriodLabel,
            recurringBillingPeriodLabel: props.recurringBillingPeriodLabel,
            nonRecurringOneMonthBillingPeriodLabel: props.nonRecurringOneMonthBillingPeriodLabel,
            nonRecurringBillingPeriodLabel: props.nonRecurringBillingPeriodLabel,
        },

        cinemaChainName: props.cinemaChainName,
        componentErrorTitle: props.componentErrorTitle,
        noSearchResultsFoundMessage: props.noSearchResultsFoundMessage,
    }
}

// CONFIG
export function getOccLocale(props: OccContextPropsApi): NestedPartial<OccLocaleConfig> {
    return {
        currency: {
            symbol: props.symbol,
            decimalPrecision: props.decimalPrecision,
            decimalSeparator: props.decimalSeparator,
            format: props.format,
            nonNumberPlaceholder: props.nonNumberPlaceholder ?? '',
            thousandsSeparator: props.thousandsSeparator,
        },
        date: {
            formats: {
                dateDefault: props.dateDefault,
                dateLong: props.dateLong,
                dateShort: props.dateShort,
                iso: props.iso,
                timeDefault: props.timeDefault,
                timeLong: props.timeLong,
                timeShort: props.timeShort,
            },
            startWeekOnSunday: props.startWeekOnSunday,
            ordinalStringFormatter: (value: number) => {
                // Todo, redo ordinals for spain and portugal

                // const absValue = Math.abs(value);
                // const rem10 = absValue % 10;
                // const rem100 = absValue % 100;
                // if (rem10 === 1 && rem100 !== 11) {
                //     return value + 'sto';
                // }
                // if (rem10 === 2 && rem100 !== 12) {
                //     return value + 'ndo';
                // }
                // if (rem10 === 3 && rem100 !== 13) {
                //     return value + 'rdo';
                // }
                // return value + 'tho';

                return value
            },
        },
        // TODO: number
        // TODO: personName
    }
}

export function getOrderConfig(props: OccContextPropsApi): NestedPartial<OrderConfig> {
    return {
        admittingDuration: props.admittingDuration,
        bookingFeeMode: props.bookingFeeExclusive ? BookingFeeMode.Exclusive : BookingFeeMode.Inclusive,
        isBookingFeeApplicableToUnpaidBookings: props.isBookingFeeApplicableToUnpaidBookings,
        maximumSeatsPerOrder: props.maximumAllowedTicketsInAnOrder,
        ticketingMode: props.isSeatFirstOrdering ? TicketingMode.SeatFirst : TicketingMode.TicketFirst,
        unpaidBookingCutoffTime: props.unpaidBookingCutoffTime,
        taxMode: props.taxModeExclusive ? TaxMode.Exclusive : TaxMode.Inclusive,
        maximumItemsPerOrder: props.maximumItemsPerOrder,
        maximumModifiersPerItem: props.maximumModifiersPerItem,
        tip: {
            isEnabled: props.tipIsEnabled,
            includeSundryItems: props.tipIncludeSundryItems,
        },
    }
}

export function getFormValidationConfig(props: OccContextPropsApi): NestedPartial<OccFormFieldValidationConfig> {
    return {
        password: {
            type: FieldFormatType.Password,
            required: true,
            maxLength: 64,
            rules: [
                {
                    displayText: props.passwordRuleCheckerUppercaseRule,
                    regExpression: '([A-ZÅÄÖ])',
                },
                {
                    displayText: props.passwordRuleCheckerLowercaseRule,
                    regExpression: '([a-zåäö])',
                },
                {
                    displayText: props.passwordRuleCheckerNumericRule,
                    regExpression: '([0-9])',
                },
                {
                    displayText: props.passwordRuleCheckerSpecialCharacterRule,
                    regExpression:
                        '[\'\\/\\!\\@#\\$\\%\\^\\&\\*\\)\\(\\+\\=\\.\\<\\>\\{\\}\\[\\]\\:\\;\\|\\~\\`\\_\\-,\\?\\¬£"]',
                },
                {
                    displayText: props.passwordRuleCheckerMinLengthRule,
                    regExpression: '.{10,}',
                },
            ],
        },
    }
}

export function getFilmConfig(props: OccContextPropsApi): OccFilmConfig {
    return {
        customActions: {},
        showWatchlistButtons: props.watchlistEnabled,
        preferredTrailerProvider: FilmTrailerProvider.External,
    }
}

export function getFilterConfig(props: OccContextPropsApi): OccFilterConfig {
    return {
        attributeIds: undefined,
        showFullAttributeName: props.showFullAttributeName,
        filmCategories: [FilmCategory.NowShowing, FilmCategory.ComingSoon, FilmCategory.AdvanceBooking],
    }
}

export function getMemberConfig(_props: OccContextPropsApi): OccMemberConfig {
    return {
        ...defaultOccMemberConfig,
    }
}

export function getNotificationConfig(props: OccContextPropsApi): OccNotificationConfig {
    return {
        dismissalTime: props.notificationDismissalTime,
    }
}

// ASSETS
export function getAssets(props: OccContextPropsApi): OccAssets {
    return {
        ...defaultOccAssets,
        componentErrorIconName: props.componentErrorIcon,
        filmWatchlistButtonIconName: props.watchlistButtonIconName,
        loadingIconName: cmsDataService.isOdeon() ? 'loading-spinner-white' : props.loadingIconName,
        modalCloseButtonIconName: 'cross-large',
        carouselPreviousButtonIconName: 'arrowLeft',
        carouselNextButtonIconName: 'arrowRight',
        zoomControllerZoomInButtonIconName: 'zoom-in',
        zoomControllerZoomOutButtonIconName: 'zoom-out',

        passwordInputHiddenIconName: props.passwordInputHiddenIcon,
        passwordInputVisibleIconName: props.passwordInputVisibleIcon,
        passwordRuleCheckerInitialIconName: props.passwordRuleCheckerInitialIconName,
        passwordRuleCheckerInvalidIconName: props.passwordRuleCheckerInvalidIconName,
        passwordRuleCheckerSuccessIconName: props.passwordRuleCheckerSuccessIconName,
        advanceBookingPeriodNotificationIconName: props.advanceBookingPeriodNotificationIconName,
    }
}

// ACCESSIBILITY
export function getOccAccessibilityText(props: OccContextPropsApi): OccAccessibilityText {
    return {
        ...defaultOccAccessibilityText,

        itemImageAltTextFormat: props.itemImageAltTextFormat,
        addToAppleWalletButtonAriaLabel: props.addToAppleWalletButtonAriaLabelFormat,

        eventGalleryImageAltTextFormat: props.eventGalleryImageAltTextFormat,
        eventPosterAltTextFormat: props.eventPosterAltTextFormat,
        filmAdvertisementAltTextFormat: props.filmAdvertisementAltTextFormat,
        filmBackdropAltTextFormat: props.filmBackdropAltTextFormat,
        filmPosterAltTextFormat: props.filmPosterAltTextFormat,
        filmStillAltTextFormat: props.filmStillAltTextFormat,
        filmTitleTreatmentAltTextFormat: props.filmTitleTreatmentAltTextFormat,
        memberPhotoAltTextFormat: props.memberPhotoAltTextFormat,
        memberPhotoWithoutNameAltTextFormat: props.memberPhotoWithoutNameAltTextFormat,
        memberRewardImageAltTextFormat: props.memberRewardImageAltTextFormat,
        modalCloseButtonAriaLabel: props.modalCloseButtonAriaLabel,
        redirectPaymentMethodButtonAriaLabelFormat: props.redirectPaymentMethodButtonAriaLabelFormat,
        redirectPaymentMethodIconAltTextFormat: props.redirectPaymentMethodIconAltTextFormat,
        removeGiftCardButtonAriaLabelFormat: props.removeGiftCardButtonAriaLabelFormat,
        removeMemberBalancePaymentButtonAriaLabelFormat: props.removeMemberBalancePaymentButtonAriaLabelFormat,
        saveToGooglePayAriaLabelFormat: props.saveToGooglePayAriaLabelFormat,

        // Seat picker
        companionSeatAriaLabelFormat: props.companionSeatAriaLabelFormat,
        sofaLeftSeatAriaLabelFormat: props.sofaLeftSeatAriaLabelFormat,
        sofaMiddleSeatAriaLabelFormat: props.sofaMiddleSeatAriaLabelFormat,
        sofaRightSeatAriaLabelFormat: props.sofaRightSeatAriaLabelFormat,
        unavailableSeatAriaLabelFormat: props.unavailableSeatAriaLabelFormat,
        wheelchairSeatAriaLabelFormat: props.wheelchairSeatAriaLabelFormat,
        normalSeatAriaLabelFormat: props.normalSeatAriaLabelFormat,
    }
}
